/**
 * rootDeriver.js — native-Arabic Arabic root derivation.
 *
 * Given an Arabic surface word (e.g. كتاب) this reproduces the derivational
 * root(s) the dictionary's morphological analyzer yields — in the same order,
 * with slash-separated root alternatives (e.g. "اخو/اخي") split out.
 *
 * The algorithm (segment into prefix/stem/suffix → check category compatibility
 * → unify the candidate's vocalized form against the input) works directly on
 * Arabic Unicode characters: there is NO Buckwalter transliteration at runtime.
 * It consumes the precompiled native-Arabic bundles in /public/data/aramorph/
 * (emitted by scripts/build_aramorph_js.py).
 *
 * Char-class definitions below are the Arabic equivalents of the classic
 * Buckwalter/AraMorph categories; the source codepoints are noted for clarity.
 * Note: alef-wasla is the real ٱ (U+0671), kept distinct from a plain alef ا
 * (U+0627) so it unifies the way an underspecified alef must.
 *
 * Authored as an ES module. It must NOT use `module.exports`: in the production
 * build Babel injects ESM imports for its runtime helpers (classes, generators,
 * async), webpack then treats the file as ESM, and assigning module.exports
 * throws at load — a blank screen. Node (>=22.12) still loads it via require()
 * for the cross-check unit test (ESM syntax detection + require(esm)).
 */

/* ------------------------------------------------------------------ *
 * Arabic character classes
 * ------------------------------------------------------------------ */

// Letters treated as consonants (incl. the long vowels ا و ى ي, the hamza
// carriers, and alef-wasla ٱ). Everything that is not a diacritic or tatweel.
//   ء آ أ ؤ إ ئ ا ٱ ب ة ت ث ج ح خ د ذ ر ز س ش ص ض ط ظ ع غ ف ق ك ل م ن ه و ى ي
const CONSONANT_CHARS =
  "ءآأؤإئاٱبةتث" +
  "جحخدذرزسشصضط" +
  "ظعغفقكلمنهوىي";

// Diacritics (harakat, tanween, shadda, sukun, dagger-alef):
//   ً ٌ ٍ َ ُ ِ ّ ْ ٰ
const VOCAL_CHARS = "ًٌٍَُِّْٰ";
// Short vocals == diacritics except shadda (ّ).
const SHORT_VOCAL_CHARS = "ًٌٍَُِْٰ";

const TATWEEL = "ـ"; // ـ
const SHADDA = "ّ"; // ّ — the sole "no-vocal" diacritic
const TANWEEN = new Set("ًٌٍ"); // ً ٌ ٍ

// Alef family that participates in alef-unification: ا آ أ إ ٱ
const PLAIN_ALEF = "ا"; // ا — underspecified alef (the Buckwalter 'A' wildcard)
const ALEF_WASLA = "ٱ"; // ٱ — (Buckwalter '{')
const ALEF_HAMZA_BELOW = "إ"; // إ — (Buckwalter '<')
const ALEF_EQUIVALENT = new Set([
  PLAIN_ALEF,
  "آ", // آ
  "أ", // أ
  ALEF_HAMZA_BELOW,
  ALEF_WASLA,
]);

const CONSONANTS = new Set(CONSONANT_CHARS);
const SHORT_VOCALS = new Set(SHORT_VOCAL_CHARS);
const NO_VOCAL_DIACRITICS = new Set([SHADDA]);

const isConsonant = (c) => CONSONANTS.has(c);
const isNoVocalDiacritic = (c) => NO_VOCAL_DIACRITICS.has(c);

/* ------------------------------------------------------------------ *
 * Unification — failures collapse the candidate to "not unifiable".
 * ------------------------------------------------------------------ */

class UnificationFailure extends Error {}
class DestructuringError extends Error {}

// consonant + optional non-vocal (shadda) diacritic + optional vocal/nunation
function destructureConsonantCluster(cluster) {
  const consonant = cluster[0];
  const v1 = cluster.length >= 2 ? cluster[1] : null;
  const v2 = cluster.length >= 3 ? cluster[2] : null;

  if (v2 !== null && v1 !== null && isNoVocalDiacritic(v1) && isNoVocalDiacritic(v2))
    throw new DestructuringError(`Invalid no vocal diacritic ${v1} and ${v2}`);
  if (v2 !== null && v1 !== null && SHORT_VOCALS.has(v1) && SHORT_VOCALS.has(v2))
    throw new DestructuringError(`Invalid vocal diacritic ${v1} and ${v2}`);

  // shadda after vocal -> swap so shadda sits in the diacritic slot
  if (v2 !== null && v1 !== null && isNoVocalDiacritic(v2)) return [consonant, v2, v1];
  if (v1 === null || isNoVocalDiacritic(v1)) return [consonant, v1, v2];
  return [consonant, null, v1];
}

function unifyAlef(c1, c2) {
  if (ALEF_EQUIVALENT.has(c1) && ALEF_EQUIVALENT.has(c2)) {
    if (c1 === PLAIN_ALEF) return c2;
    if (c2 === PLAIN_ALEF) return c1;
    if (c1 === c2) return c1;
    if ((c1 === ALEF_WASLA && c2 === ALEF_HAMZA_BELOW) ||
        (c1 === ALEF_HAMZA_BELOW && c2 === ALEF_WASLA))
      return ALEF_HAMZA_BELOW;
    throw new UnificationFailure(`unify_alef failed for ${c1} and ${c2}`);
  }
  throw new UnificationFailure(`non-alef character in ${c1} or ${c2}`);
}

function unifyConsonant(c1, c2) {
  try {
    return unifyAlef(c1, c2);
  } catch (e) {
    if (!(e instanceof UnificationFailure)) throw e;
    if (c1 === c2) return c1;
    throw new UnificationFailure(`unify_consonant failed for ${c1} and ${c2}`);
  }
}

function unifyChar(c1, c2) {
  if (c1 === null) return c2;
  if (c2 === null) return c1;
  if (c1 === c2) return c1;
  throw new UnificationFailure(`unify_char failed for ${c1} and ${c2}`);
}

function unifyConsonantCluster(c1, c2) {
  const [cons1, d1, v1] = destructureConsonantCluster(c1);
  const [cons2, d2, v2] = destructureConsonantCluster(c2);
  return [unifyConsonant(cons1, cons2), unifyChar(d1, d2), unifyChar(v1, v2)];
}

function getConsonantClusters(form) {
  if (form === "") return [];
  if (!isConsonant(form[0]))
    throw new DestructuringError(`non-consonant start of word form ${form}`);

  const indexes = [];
  for (let i = 0; i < form.length; i++) if (isConsonant(form[i])) indexes.push(i);
  indexes.push(form.length);

  const clusters = [];
  for (let i = 0; i < indexes.length - 1; i++) clusters.push(form.slice(indexes[i], indexes[i + 1]));
  return clusters;
}

// part is always false for our use; returns the unified spans.
function unifyWordForms(w1, w2, part = false) {
  const c1 = getConsonantClusters(w1);
  const c2 = getConsonantClusters(w2);

  if (!part && c1.length !== c2.length)
    throw new UnificationFailure("inconsistent number of consonant clusters");
  if (c1.length === 0 || c2.length === 0)
    throw new DestructuringError("empty word form");

  let leftEnd = 0;
  let rightEnd = 0;
  const n = Math.min(c1.length, c2.length); // == lengths when part=false
  for (let i = 0; i < n; i++) {
    unifyConsonantCluster(c1[i], c2[i]); // throws on failure -> gates the match
    leftEnd += c1[i].length;
    rightEnd += c2[i].length;
  }
  return { leftEnd, rightEnd };
}

const SHORT_POS_TANWEEN_BLOCKED = new Set(["VERB", "PREP", "CONJ", "PRON"]);

// word: { vocForm, shortPos }, form: input Arabic string. filter_diacritics = true.
function isWordAndFormUnifiable(word, form) {
  try {
    const wordForm = word.vocForm;
    const result = unifyWordForms(wordForm, form, false);

    if (form.includes(SHADDA) && !wordForm.includes(SHADDA)) return false;

    const sp = word.shortPos;
    if (form.length > 0 && sp != null) {
      if (SHORT_POS_TANWEEN_BLOCKED.has(sp) && TANWEEN.has(form[form.length - 1]))
        return false;
    }

    return result.leftEnd === wordForm.length && result.rightEnd === form.length;
  } catch (e) {
    if (e instanceof UnificationFailure || e instanceof DestructuringError) return false;
    throw e;
  }
}

/* ------------------------------------------------------------------ *
 * Segmentation + analysis
 * ------------------------------------------------------------------ */

const MAX_PREFIX_LEN = 4;
const MAX_STEM_LEN = 10;
const MAX_SUFFIX_LEN = 6;

function* segmentWord(word) {
  const len = word.length;
  for (let prefLen = 0; prefLen <= MAX_PREFIX_LEN; prefLen++) {
    for (let stemLen = 1; stemLen <= MAX_STEM_LEN; stemLen++) {
      for (let suffLen = 0; suffLen <= MAX_SUFFIX_LEN; suffLen++) {
        if (prefLen + stemLen + suffLen === len) {
          yield [
            word.slice(0, prefLen),
            word.slice(prefLen, prefLen + stemLen),
            word.slice(prefLen + stemLen),
          ];
        }
      }
    }
  }
}

/**
 * Build an analyzer bound to the precompiled native-Arabic bundles.
 *   data = { stems, prefixes, suffixes, compat: { ab, ac, bc } }
 *   - stems:    { form: [[vocForm, cat, root, shortPos], ...] }  (root-bearing only)
 *   - prefixes: { form: [[vocForm, cat], ...] }
 *   - suffixes: { form: [[vocForm, cat], ...] }
 *   - compat.*: array of "catA catB" compatibility pairs
 * All form/vocForm/root values are Arabic; cat/shortPos are ASCII tags.
 */
function buildAnalyzer(data) {
  const stems = data.stems || {};
  const prefixes = data.prefixes || {};
  const suffixes = data.suffixes || {};
  const ab = data.compat.ab instanceof Set ? data.compat.ab : new Set(data.compat.ab);
  const ac = data.compat.ac instanceof Set ? data.compat.ac : new Set(data.compat.ac);
  const bc = data.compat.bc instanceof Set ? data.compat.bc : new Set(data.compat.bc);

  // analyze(): yields candidate Words (only the fields root derivation needs)
  function analyze(word) {
    let consonants = "";
    for (const c of word) if (isConsonant(c)) consonants += c;

    const out = [];
    for (const [pref, stem, suff] of segmentWord(consonants)) {
      const stemList = stems[stem];
      if (!stemList) continue; // no root-bearing stem with this form
      const prefList = prefixes[pref];
      if (!prefList) continue;
      const suffList = suffixes[suff];
      if (!suffList) continue;

      for (const [pVoc, pCat] of prefList) {
        for (const [sVoc, sCat, sRoot, sSp] of stemList) {
          if (!ab.has(pCat + " " + sCat)) continue;
          for (const [fVoc, fCat] of suffList) {
            if (!ac.has(pCat + " " + fCat)) continue;
            if (!bc.has(sCat + " " + fCat)) continue;
            // voc_form() joins the non-empty vocalised forms
            let vocForm = "";
            if (pVoc) vocForm += pVoc;
            if (sVoc) vocForm += sVoc;
            if (fVoc) vocForm += fVoc;
            out.push({ vocForm, shortPos: sSp, root: sRoot, prefix: pref, stem, suffix: suff });
          }
        }
      }
    }
    return out;
  }

  /**
   * analyzeRoots(arabicWord) -> every analysis that unifies with the input, in
   * analyzer order, one row per "a/b" root alternative:
   *   { root, prefix, stem, suffix }  (the consonant segmentation of the input).
   * Lets callers rank roots without changing them.
   */
  function analyzeRoots(arabicWord) {
    const out = [];
    for (const w of analyze(arabicWord)) {
      if (!isWordAndFormUnifiable(w, arabicWord)) continue;
      const r = w.root;
      if (!r) continue;
      for (const part of r.split("/")) {
        if (part) {
          out.push({ root: part, prefix: w.prefix, stem: w.stem, suffix: w.suffix });
        }
      }
    }
    return out;
  }

  /**
   * deriveRoots(arabicWord) -> ordered, de-duplicated list of derived roots
   * (Arabic), with "a/b" root alternatives split out.
   */
  function deriveRoots(arabicWord) {
    const out = [];
    const seen = new Set();
    for (const { root } of analyzeRoots(arabicWord)) {
      if (!seen.has(root)) {
        seen.add(root);
        out.push(root);
      }
    }
    return out;
  }

  return { analyze, analyzeRoots, deriveRoots };
}

/* ------------------------------------------------------------------ *
 * Browser-facing lazy loader (fetches the bundles once, then memoises)
 * ------------------------------------------------------------------ */

let _analyzerPromise = null;

async function loadRootDeriver(base = "/data/aramorph") {
  if (!_analyzerPromise) {
    _analyzerPromise = (async () => {
      const [stems, affixes, compat] = await Promise.all([
        fetch(`${base}/stems.json`).then((r) => r.json()),
        fetch(`${base}/affixes.json`).then((r) => r.json()),
        fetch(`${base}/compat.json`).then((r) => r.json()),
      ]);
      return buildAnalyzer({
        stems,
        prefixes: affixes.prefixes,
        suffixes: affixes.suffixes,
        compat,
      });
    })().catch((e) => {
      _analyzerPromise = null; // allow retry on transient failure
      throw e;
    });
  }
  return _analyzerPromise;
}

/** Convenience: derive roots in the browser, loading bundles on first use. */
async function deriveRoots(arabicWord) {
  const analyzer = await loadRootDeriver();
  return analyzer.deriveRoots(arabicWord);
}

/** Convenience: per-analysis root rows (see analyzeRoots), loading bundles on first use. */
async function analyzeRoots(arabicWord) {
  const analyzer = await loadRootDeriver();
  return analyzer.analyzeRoots(arabicWord);
}

export { buildAnalyzer, loadRootDeriver, deriveRoots, analyzeRoots };
